/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { PagedAsyncIterableIterator } from "@azure/core-paging";
import { RegulatoryComplianceControls } from "../operationsInterfaces";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers";
import * as Parameters from "../models/parameters";
import { SecurityCenter } from "../securityCenter";
import {
  RegulatoryComplianceControl,
  RegulatoryComplianceControlsListNextOptionalParams,
  RegulatoryComplianceControlsListOptionalParams,
  RegulatoryComplianceControlsListResponse,
  RegulatoryComplianceControlsGetOptionalParams,
  RegulatoryComplianceControlsGetResponse,
  RegulatoryComplianceControlsListNextResponse
} from "../models";

/// <reference lib="esnext.asynciterable" />
/** Class containing RegulatoryComplianceControls operations. */
export class RegulatoryComplianceControlsImpl
  implements RegulatoryComplianceControls {
  private readonly client: SecurityCenter;

  /**
   * Initialize a new instance of the class RegulatoryComplianceControls class.
   * @param client Reference to the service client
   */
  constructor(client: SecurityCenter) {
    this.client = client;
  }

  /**
   * All supported regulatory compliance controls details and state for selected standard
   * @param regulatoryComplianceStandardName Name of the regulatory compliance standard object
   * @param options The options parameters.
   */
  public list(
    regulatoryComplianceStandardName: string,
    options?: RegulatoryComplianceControlsListOptionalParams
  ): PagedAsyncIterableIterator<RegulatoryComplianceControl> {
    const iter = this.listPagingAll(regulatoryComplianceStandardName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: () => {
        return this.listPagingPage(regulatoryComplianceStandardName, options);
      }
    };
  }

  private async *listPagingPage(
    regulatoryComplianceStandardName: string,
    options?: RegulatoryComplianceControlsListOptionalParams
  ): AsyncIterableIterator<RegulatoryComplianceControl[]> {
    let result = await this._list(regulatoryComplianceStandardName, options);
    yield result.value || [];
    let continuationToken = result.nextLink;
    while (continuationToken) {
      result = await this._listNext(
        regulatoryComplianceStandardName,
        continuationToken,
        options
      );
      continuationToken = result.nextLink;
      yield result.value || [];
    }
  }

  private async *listPagingAll(
    regulatoryComplianceStandardName: string,
    options?: RegulatoryComplianceControlsListOptionalParams
  ): AsyncIterableIterator<RegulatoryComplianceControl> {
    for await (const page of this.listPagingPage(
      regulatoryComplianceStandardName,
      options
    )) {
      yield* page;
    }
  }

  /**
   * All supported regulatory compliance controls details and state for selected standard
   * @param regulatoryComplianceStandardName Name of the regulatory compliance standard object
   * @param options The options parameters.
   */
  private _list(
    regulatoryComplianceStandardName: string,
    options?: RegulatoryComplianceControlsListOptionalParams
  ): Promise<RegulatoryComplianceControlsListResponse> {
    return this.client.sendOperationRequest(
      { regulatoryComplianceStandardName, options },
      listOperationSpec
    );
  }

  /**
   * Selected regulatory compliance control details and state
   * @param regulatoryComplianceStandardName Name of the regulatory compliance standard object
   * @param regulatoryComplianceControlName Name of the regulatory compliance control object
   * @param options The options parameters.
   */
  get(
    regulatoryComplianceStandardName: string,
    regulatoryComplianceControlName: string,
    options?: RegulatoryComplianceControlsGetOptionalParams
  ): Promise<RegulatoryComplianceControlsGetResponse> {
    return this.client.sendOperationRequest(
      {
        regulatoryComplianceStandardName,
        regulatoryComplianceControlName,
        options
      },
      getOperationSpec
    );
  }

  /**
   * ListNext
   * @param regulatoryComplianceStandardName Name of the regulatory compliance standard object
   * @param nextLink The nextLink from the previous successful call to the List method.
   * @param options The options parameters.
   */
  private _listNext(
    regulatoryComplianceStandardName: string,
    nextLink: string,
    options?: RegulatoryComplianceControlsListNextOptionalParams
  ): Promise<RegulatoryComplianceControlsListNextResponse> {
    return this.client.sendOperationRequest(
      { regulatoryComplianceStandardName, nextLink, options },
      listNextOperationSpec
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/providers/Microsoft.Security/regulatoryComplianceStandards/{regulatoryComplianceStandardName}/regulatoryComplianceControls",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.RegulatoryComplianceControlList
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.filter, Parameters.apiVersion8],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.regulatoryComplianceStandardName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const getOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/providers/Microsoft.Security/regulatoryComplianceStandards/{regulatoryComplianceStandardName}/regulatoryComplianceControls/{regulatoryComplianceControlName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.RegulatoryComplianceControl
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion8],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.regulatoryComplianceStandardName,
    Parameters.regulatoryComplianceControlName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const listNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.RegulatoryComplianceControlList
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.filter, Parameters.apiVersion8],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.nextLink,
    Parameters.regulatoryComplianceStandardName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
